import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  usePaymentFailureGracePeriodDefaultQuery,
  useUpdateVenueGracePeriodMutation,
  useVenueGracePeriodInfoLazyQuery,
  useVenuesQuery,
} from '../../generated/graphql';

const getStatusBgColor = (isLockedOut: boolean, hasPaymentFailure: boolean) => {
  if (isLockedOut) return '#FFEBEE';
  if (hasPaymentFailure) return '#FFF8E1';
  return '#F3F6FF';
};

export const GracePeriodManager = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [newGracePeriodDays, setNewGracePeriodDays] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const {
    data: venuesData,
    loading: venuesLoading,
    error: venuesError,
  } = useVenuesQuery({ fetchPolicy: 'cache-and-network' });
  const { data: defaultGracePeriodData, loading: defaultGracePeriodLoading } =
    usePaymentFailureGracePeriodDefaultQuery({
      fetchPolicy: 'cache-and-network',
    });

  const [loadGracePeriodInfo, { data: gracePeriodData, loading: infoLoading }] =
    useVenueGracePeriodInfoLazyQuery();

  const [updateGracePeriod, { loading: updating }] =
    useUpdateVenueGracePeriodMutation();

  const venues = useMemo(() => {
    if (!venuesData?.venues) return [];
    return [...venuesData.venues].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;

  const handleVenueChange = (
    _: React.SyntheticEvent,
    value: { id: string; name: string } | null,
  ) => {
    const venueId = value?.id ?? '';
    setSelectedVenueId(venueId);
    setHasLoaded(false);
    setNewGracePeriodDays('');
    if (venueId) {
      loadGracePeriodInfo({ variables: { venueId } }).then((result) => {
        const info = result.data?.venueGracePeriodInfo;
        if (info) {
          const defaultDays =
            defaultGracePeriodData?.paymentFailureGracePeriodDefault;
          let nextGracePeriodValue = '';
          if (info.gracePeriodDays != null) {
            nextGracePeriodValue = String(info.gracePeriodDays);
          } else if (defaultDays != null) {
            nextGracePeriodValue = String(defaultDays);
          }
          setNewGracePeriodDays(nextGracePeriodValue);
          setHasLoaded(true);
        }
      });
    }
  };

  const handleUpdate = async () => {
    if (!selectedVenueId) return;
    const parsedDays =
      newGracePeriodDays !== '' ? Number(newGracePeriodDays) : null;
    await updateGracePeriod({
      variables: {
        venueId: selectedVenueId,
        gracePeriodDays: parsedDays,
      },
      toastConfig: {
        successText: 'Grace period updated successfully',
        errorText: 'Failed to update grace period',
      },
    });
    loadGracePeriodInfo({ variables: { venueId: selectedVenueId } });
  };

  const info = gracePeriodData?.venueGracePeriodInfo;
  const globalDefaultGracePeriodDays =
    defaultGracePeriodData?.paymentFailureGracePeriodDefault;
  let globalDefaultLabel = 'not available';
  if (defaultGracePeriodLoading) {
    globalDefaultLabel = 'loading...';
  }
  if (globalDefaultGracePeriodDays != null) {
    globalDefaultLabel = `${globalDefaultGracePeriodDays}`;
  }

  const gracePeriodDaysError =
    newGracePeriodDays !== '' &&
    (Number(newGracePeriodDays) < 1 || Number(newGracePeriodDays) > 365);

  return (
    <Box width="100%" p={3} display="flex" justifyContent="center">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 2,
          maxWidth: '1200px',
          width: '100%',
        }}
      >
        <Card sx={{ p: 3, backgroundColor: '#FAFBFF', minWidth: '500px' }}>
          <Typography variant="h3" gutterBottom>
            Payment Failure Grace Period
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set how many days a venue has before being locked out after a
            payment failure. The global default is {globalDefaultLabel} days.
          </Typography>

          <Autocomplete
            fullWidth
            options={venues}
            getOptionLabel={(o) => o.name}
            loading={venuesLoading}
            value={selectedVenue}
            onChange={handleVenueChange}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText={
              venuesError ? 'Error loading venues' : 'No venues found'
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Venue"
                variant="outlined"
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: venuesLoading ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
              />
            )}
          />

          {infoLoading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={28} />
            </Box>
          )}

          {hasLoaded && info && (
            <>
              <Box
                mt={2}
                p={2}
                bgcolor={getStatusBgColor(
                  info.isLockedOut,
                  info.hasPaymentFailure,
                )}
                borderRadius={1}
                border={info.hasPaymentFailure ? 1 : 0}
                borderColor={info.isLockedOut ? 'error.light' : 'warning.light'}
              >
                <Typography variant="body2">
                  <strong>Has failed payment currently</strong>{' '}
                  {info.hasPaymentFailure ? 'Yes' : 'No'}
                </Typography>

                {info.hasPaymentFailure && info.paymentFailureStartedAt && (
                  <Typography variant="body2">
                    <strong>Failed payment since:</strong>{' '}
                    {new Date(info.paymentFailureStartedAt).toLocaleDateString(
                      'en-GB',
                      { day: '2-digit', month: 'short', year: 'numeric' },
                    )}
                  </Typography>
                )}

                <Typography variant="body2">
                  <strong>Current failed payment grace period:</strong>{' '}
                  {info.gracePeriodDays ?? globalDefaultLabel} days
                  {info.gracePeriodDays == null && ' (global default)'}
                </Typography>

                <Typography variant="body2">
                  <strong>Billing type:</strong>{' '}
                  {info.paymentType
                    ? info.paymentType.charAt(0).toUpperCase() +
                      info.paymentType.slice(1)
                    : '—'}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" gutterBottom>
                Update Grace Period
              </Typography>

              <Tooltip
                title={
                  `Leave at global default (${globalDefaultLabel} days) ` +
                  'or enter a custom number of days (1-365). Clear the field to use the default.'
                }
                arrow
              >
                <TextField
                  fullWidth
                  label="Grace Period (days)"
                  type="number"
                  value={newGracePeriodDays}
                  onChange={(e) => setNewGracePeriodDays(e.target.value)}
                  inputProps={{ min: 1, max: 365 }}
                  error={gracePeriodDaysError}
                  helperText={
                    gracePeriodDaysError
                      ? 'Enter a value between 1 and 365'
                      : `Leave empty or set to ${globalDefaultLabel} to use global default`
                  }
                  sx={{ mt: 1 }}
                />
              </Tooltip>

              <Button
                variant="contained"
                sx={{ mt: 2 }}
                disabled={updating || gracePeriodDaysError}
                onClick={handleUpdate}
              >
                {updating ? <CircularProgress size={20} /> : 'Update'}
              </Button>
            </>
          )}
        </Card>

        <Card sx={{ p: 3, backgroundColor: '#FAFBFF' }}>
          <Typography variant="h6" gutterBottom>
            About Grace Period
          </Typography>
          <Typography variant="body2" gutterBottom>
            The grace period is the number of days a venue can continue using
            the app after a payment failure before being locked out.
          </Typography>
          <Typography variant="body2" gutterBottom>
            During the grace period, the user sees a warning banner. After the
            grace period expires, the app is blocked with a full-screen modal.
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Global default:</strong> {globalDefaultLabel} days
          </Typography>
          <Typography variant="body2" gutterBottom>
            Setting a custom value overrides the global default for that venue
            only. To reset to the global default, clear the field and save.
          </Typography>
        </Card>
      </Box>
    </Box>
  );
};
